import { ArrowRight, Shield, Clock, HelpCircle, Sparkles, BookOpen, MessageSquare, ListChecks, Users, DollarSign, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useWizard } from './WizardContext';
import { Loader2 } from 'lucide-react';
import { useSearchHistory } from '@/hooks/useSearchHistory';
import { SearchHistory } from '@/components/SearchHistory';

export const IntroScreen = () => {
  const { goToNext } = useWizard();
  const navigate = useNavigate();
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { history, addSearch, removeSearch, clearHistory } = useSearchHistory();

  const handleAiSearch = () => {
    if (!query.trim() || query.trim().length < 5) return;
    
    setIsLoading(true);
    // Save to history before navigating
    addSearch(query.trim());
    // Navigate to AI search results page with query
    navigate(`/ai-search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleHistorySelect = (selectedQuery) => {
    setQuery(selectedQuery);
    // Optionally auto-search
    addSearch(selectedQuery);
    navigate(`/ai-search?q=${encodeURIComponent(selectedQuery)}`);
  };

  const guideFeatures = [
    { icon: Lock, label: 'Security requirements' },
    { icon: Users, label: 'Team size' },
    { icon: DollarSign, label: 'Budget preferences' },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero section - SEO Optimized */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center mb-6">
          <img 
            src="/logo-square.png" 
            alt="GoCompareAI - Compare AI Platforms" 
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl shadow-lg"
          />
        </div>
        
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-4 leading-tight">
          Compare AI platforms with confidence
        </h1>
        
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          GoCompareAI is an independent platform that helps businesses and individuals compare AI tools side by side. 
          Choose how you'd like to find the right AI for your needs.
        </p>
      </div>

      {/* Two Path Options */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        
        {/* AI Assistant Card - Modern Design with Blue Theme */}
        <Card 
          className={`relative overflow-hidden border-0 transition-all cursor-pointer hover:shadow-2xl ${
            showAiAssistant 
              ? 'shadow-2xl ring-2 ring-sky-400/50' 
              : 'shadow-lg hover:scale-[1.02]'
          }`}
          style={{
            background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 50%, #38BDF8 100%)'
          }}
          onClick={() => !showAiAssistant && setShowAiAssistant(true)}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-16 translate-x-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12" />
          <div className="absolute top-1/2 right-4 w-2 h-2 bg-sky-600/30 rounded-full animate-pulse" />
          <div className="absolute top-1/3 right-8 w-1.5 h-1.5 bg-sky-600/20 rounded-full animate-pulse delay-300" />
          
          <CardContent className="relative p-6 z-10">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-sky-500/30">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-sky-900 mb-1">Not sure? Ask our AI</h3>
                <p className="text-sky-800/80 text-sm">
                  Describe what you want in plain English — our AI will find the perfect platforms for you
                </p>
              </div>
            </div>
            
            {!showAiAssistant ? (
              <Button 
                className="w-full gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-sky-500/25 border-0 h-12 text-base font-semibold"
                onClick={(e) => { e.stopPropagation(); setShowAiAssistant(true); }}
              >
                <Sparkles className="w-5 h-5" />
                Try AI Assistant
              </Button>
            ) : (
              <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="relative">
                  <Textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g., I want to make an animation and narrate over it, or I need help writing blog posts..."
                    className="min-h-[100px] resize-none bg-white/90 backdrop-blur-sm border-sky-200 focus:border-sky-400 focus:ring-sky-400/20 placeholder:text-sky-700/40 text-sky-900"
                    data-testid="ai-search-input"
                  />
                </div>
                <Button 
                  className="w-full gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-sky-500/25 border-0 h-12 text-base font-semibold"
                  onClick={handleAiSearch}
                  disabled={isLoading || query.trim().length < 5}
                  data-testid="ai-search-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Finding platforms...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Find platforms for me
                    </>
                  )}
                </Button>
                
                {/* Search History */}
                <SearchHistory
                  history={history}
                  onSelect={handleHistorySelect}
                  onRemove={removeSearch}
                  onClear={clearHistory}
                  variant="compact"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Start Guide Card - Modern Design */}
        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 50%, #6EE7B7 100%)'
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-16 translate-x-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12" />
          
          <CardContent className="relative p-6 z-10">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
                <ListChecks className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-emerald-900 mb-1">Start Guided Comparison</h3>
                <p className="text-emerald-800/80 text-sm">
                  Answer quick questions to get tailored recommendations based on your specific needs
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-xs text-emerald-700 mb-2 font-medium">Perfect if you know details like:</p>
              <div className="flex flex-wrap gap-2">
                {guideFeatures.map((feature, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/60 backdrop-blur-sm rounded-full text-xs text-emerald-700 font-medium shadow-sm">
                    <feature.icon className="w-3 h-3" />
                    {feature.label}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-xs text-emerald-700 mb-4">
              <span className="flex items-center gap-1.5 bg-white/40 px-2.5 py-1 rounded-full">
                <Clock className="w-3.5 h-3.5" />
                30-60 seconds
              </span>
              <span className="flex items-center gap-1.5 bg-white/40 px-2.5 py-1 rounded-full">
                <Shield className="w-3.5 h-3.5" />
                No login required
              </span>
            </div>
            
            <Button 
              className="w-full gap-2 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white shadow-lg shadow-emerald-500/25 border-0 h-12 text-base font-semibold"
              onClick={goToNext}
            >
              Start Guide
              <ArrowRight className="w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Secondary CTA - Learn about AI */}
      <div className="flex flex-col items-center gap-4">
        <Button
          variant="outline"
          asChild
          className="gap-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 hover:from-emerald-100 hover:to-teal-100 hover:text-emerald-800 hover:border-emerald-300 shadow-sm"
        >
          <Link to="/learn">
            <BookOpen className="w-4 h-4" />
            Learn about AI and read Blogs
          </Link>
        </Button>
        
        <p className="text-xs text-muted-foreground text-center max-w-md">
          This tool provides general information to help you explore options. 
          Results are based on the information you provide and may not reflect current offerings.
        </p>
      </div>
    </div>
  );
};
